/**
 * @typedef {Object} PopinOptions
 * @property {string} id
 * @property {string} [title]
 * @property {string} [message]
 * @property {string} [confirmButton]
 * @property {string} [cancelButton]
 * @property {function} [onConfirm]
 * @property {function} [onCancel]
 */

class Popin {
  /**
   * @param {Element} rootElement
   * @param {HTMLTemplateElement} template
   * @param {PopinOptions} options
   */
  constructor(rootElement, template, options) {
    /** @type {Element} */
    this.element = rootElement;
    /** @type {HTMLTemplateElement} */
    this._template = template;
    /** @type {PopinOptions} */
    this.options = options;
    this._defaultOptions = {
      title: 'Êtes-vous sûr(e) ?',
      message: 'Voulez-vous vraiment effectuer cette action ?',
      confirmButton: 'Valider',
      cancelButton: 'Annuler',
    };

    const {wrapper, overlay} = this._createMarkup();
    this._wrapper = wrapper;
    this._overlay = overlay;

    this.element.addEventListener(this.isForm() ? 'submit' : 'click', this.open.bind(this));
  }

  /**
   * @param {SubmitEvent|MouseEvent} event
   */
  open(event = null) {
    if (event) {
      event.preventDefault();
    }

    document.body.classList.add('not-scrollable');
    this._insertMarkup();
  }

  /**
   * @param {boolean} confirm
   */
  close(confirm) {
    this._removeMarkup();
    document.body.classList.remove('not-scrollable');

    if (confirm && this.options.onConfirm) {
      this.options.onConfirm();
    }
    if (!confirm && this.options.onCancel) {
      this.options.onCancel();
    }

    if (!confirm) {
      return;
    }

    if (this.isForm()) {
      this.element.submit();
      return;
    }

    if (this.isAnchor()) {
      this.element.cloneNode(true).click();
    }
  }

  /**
   * @returns {boolean}
   */
  isForm() {
    return this.element instanceof HTMLFormElement;
  }

  /**
   * @returns {boolean}
   */
  isAnchor() {
    return this.element instanceof HTMLAnchorElement;
  }

  /**
   * @returns {{wrapper: Node, overlay: Node}}
   * @private
   */
  _createMarkup() {
    const fragment = this._template.content.cloneNode(true);
    const confirmButton = fragment.querySelector('.save-popin-link');
    const cancelButton = fragment.querySelector('.cancel-popin-link');
    const closeButton = fragment.querySelector('.close-popin');
    const overlay = fragment.querySelector('.popin-overlay');

    fragment.querySelector('.popin-title').textContent = this._getOptions('title');
    fragment.querySelector('.popin-message').textContent = this._getOptions('message');
    confirmButton.textContent = this._getOptions('confirmButton');
    cancelButton.textContent = this._getOptions('cancelButton');

    confirmButton.addEventListener('click', () => this.close(true));
    cancelButton.addEventListener('click', () => this.close(false));
    closeButton.addEventListener('click', () => this.close(false));
    overlay.addEventListener('click', () => this.close(false));

    return {
      wrapper: fragment.querySelector('.popin-wrapper'),
      overlay: fragment.querySelector('.popin-overlay'),
    };
  }

  /**
   * @private
   */
  _insertMarkup() {
    document.body.appendChild(this._wrapper);
    document.body.appendChild(this._overlay);
  }

  /**
   * @private
   */
  _removeMarkup() {
    document.body.removeChild(this._wrapper);
    document.body.removeChild(this._overlay);
  }

  /**
   * @param {string} name
   * @returns {*}
   * @private
   */
  _getOptions(name) {
    return this.options[name] || this._defaultOptions[name];
  }
}

class PopinFactory {
  constructor() {
    /** @type {HTMLTemplateElement} */
    this._template = null;
    /** @type {Map<string, Popin>} */
    this._popins = new Map();

    document.addEventListener('DOMContentLoaded', () => {
      this._template = document.querySelector('#popinV2-template');

      document.querySelectorAll('[data-popin]')
        .forEach(element => this.create(element, {
          id: element.dataset.popin,
          title: element.dataset.popinTitle,
          message: element.dataset.popinMessage,
          confirmButton: element.dataset.popinConfirm,
          cancelButton: element.dataset.popinCancel,
        }));
    });
  }

  /**
   * @param {Element} element
   * @param {PopinOptions} options
   * @returns {Popin}
   */
  create(element, options) {
    const popin = new Popin(element, this._template, options);
    this._popins.set(options.id, popin);
    return popin;
  }

  /**
   * @param {string} id
   * @returns {Popin}
   */
  get(id) {
    const popin = this._popins.get(id);
    if (!popin) {
      throw new Error(`Popin with id ${id} not found`);
    }
    return popin;
  }
}

const popinFactory = new PopinFactory();
export default popinFactory;

/**
 * @param {Element} element
 * @param {PopinOptions} options
 * @returns {Popin}
 */
export function createPopin(element, options) {
  return popinFactory.create(element, options);
}

/**
 * @param {string} id
 * @returns {Popin}
 */
export function getPopin(id) {
  return popinFactory.get(id);
}
